/**
 * Motion tokens (prompt.md §40 `design-tokens`).
 *
 * WHY durations and easings are data: a streaming transcript animates tokens in
 * while a modal scales in, and both must obey the same "fast/standard/expressive"
 * vocabulary so the product feels deliberate. Android maps these onto Compose
 * animation specs (including the spring definitions below); web maps them onto
 * CSS transitions. Neither platform defines its own timing.
 */

/** Durations in milliseconds. */
export const MOTION_DURATIONS = {
  instant: 0,
  fast: 120,
  normal: 200,
  slow: 320,
  /** Used for overlays and other decorative, non-blocking transitions. */
  deliberate: 480,
} as const;

export type MotionDurationToken = keyof typeof MOTION_DURATIONS;

/** Cubic bezier control points, kept as numbers so any platform can consume them. */
export interface MotionEasingToken {
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
  readonly description: string;
}

export const MOTION_EASINGS: Readonly<Record<string, MotionEasingToken>> = {
  standard: { x1: 0.2, y1: 0, x2: 0, y2: 1, description: "most transitions: enter and exit" },
  decelerate: { x1: 0, y1: 0, x2: 0.2, y2: 1, description: "something arriving on screen" },
  accelerate: { x1: 0.4, y1: 0, x2: 1, y2: 1, description: "something leaving the screen" },
  linear: { x1: 0, y1: 0, x2: 1, y2: 1, description: "progress and streaming indicators" },
} as const;

export type MotionEasingName = keyof typeof MOTION_EASINGS;

/** Spring parameters for platforms with a physics-based animation engine. */
export interface MotionSpringToken {
  readonly stiffness: number;
  readonly dampingRatio: number;
  readonly description: string;
}

export const MOTION_SPRINGS: Readonly<Record<string, MotionSpringToken>> = {
  gentle: { stiffness: 220, dampingRatio: 0.9, description: "panels sliding in" },
  snappy: { stiffness: 500, dampingRatio: 0.85, description: "buttons and toggles responding" },
  bouncy: { stiffness: 320, dampingRatio: 0.55, description: "delightful, non-blocking confirmations" },
} as const;

export type MotionSpringName = keyof typeof MOTION_SPRINGS;

/** CSS shorthand for one animation step. Web-only adapter. */
export function cssTransition(property: string, duration: MotionDurationToken, easing: MotionEasingName): string {
  return `${property} ${MOTION_DURATIONS[duration]}ms cubic-bezier(${cssCubicBezier(easing)})`;
}

export function cssCubicBezier(name: MotionEasingName): string {
  const token = MOTION_EASINGS[name];
  if (!token) throw new TypeError(`unknown easing "${name}"`);
  return `${token.x1}, ${token.y1}, ${token.x2}, ${token.y2}`;
}
